/**
 * AvisoVersionNueva -- el aviso discreto de "hay una version nueva del panel" (PANEL-VERSION-NUEVA-T1).
 *
 * AUDIENCIA: cualquiera con el panel autenticado abierto (dueno, admin), escritorio o celular. Se
 * monta desde showApp(); la ficha publica del onboarding entra por showPublic() y NO lo monta
 * (limite del ASSIGN #17224: ahi el aviso tapaba el campo en el celular).
 *
 * Reusa el estilo del toast que ya existia (#nx-sw-toast, base.css). Nunca recarga solo: el boton
 * Recargar lo hace quien mira, cuando quiere. "Luego" lo esconde por esta sesion.
 *
 * Consulta /version.json como mucho cada 5 minutos: a intervalos y al volver a la pestana, con el
 * mismo freno para los dos (regla en application/version/versionNueva.js).
 *
 * @module presentation/components/AvisoVersionNueva
 */

import { crearVigia, CADA_MS, REINTENTO_MS, queHacerPorModulo, modoDeAviso, puedeRecargarAlNavegar } from '../../application/version/versionNueva.js';
import { AppConfig } from '../../config.js';

/**
 * AVISO-VERSION-CLIENTE-T1: lo que una pantalla tiene sin guardar. Cada vista que guarda algo en memoria registra una
 * pregunta (true = hay algo sin guardar); mientras alguna diga que si, la version nueva no entra sola.
 */
const preguntasSinGuardar = new Set();
export function registrarSinGuardar(pregunta) { preguntasSinGuardar.add(pregunta); }
const haySinGuardar = () => [...preguntasSinGuardar].some((p) => { try { return !!p(); } catch { return true; } });

/* global __APP_COMMIT__, __APP_VERSION__ */
const CORRIENDO = {
  commit: typeof __APP_COMMIT__ !== 'undefined' ? __APP_COMMIT__ : 'local',
  version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : ''
};

let iniciado = false;

async function leerVersionServida() {
  const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) return null;
  return res.json();
}

function mostrarAviso(servida) {
  if (document.getElementById('nx-sw-toast')) return;
  const toast = document.createElement('div');
  toast.id = 'nx-sw-toast';
  toast.setAttribute('data-version-nueva', String(servida?.commit || ''));
  toast.setAttribute('role', 'status');
  toast.innerHTML = `
    <span>Hay una versión nueva del panel${servida?.version ? ` (${String(servida.version).replace(/[<>&"]/g, '')})` : ''}</span>
    <button id="nx-version-recargar">Recargar</button>
    <button id="nx-version-luego" title="Recordármelo luego">Luego</button>`;
  document.body.appendChild(toast);
  document.getElementById('nx-version-recargar').onclick = () => window.location.reload();
  document.getElementById('nx-version-luego').onclick = () => toast.remove();
}

/**
 * FRONT-OBSTRUCCIONES-T1: el aviso de version (#nx-sw-toast) no flota: va como franja en el flujo, justo debajo de la
 * barra superior, y empuja el contenido en vez de taparlo (medido 2.2.636: tapaba tarjetas del kiosco, del inicio y
 * acciones del armador). Lo crean dos lugares (index.html por el service worker, y este modulo), los dos al final del
 * body; aqui se lo reubica en cuanto aparece, sin tocar a quien lo crea.
 */
export function ubicarAvisoVersion() {
  const mover = () => {
    const toast = document.getElementById('nx-sw-toast');
    if (!toast || toast.dataset.ubicado === '1') return;
    const nav = document.getElementById('nx-nav');
    if (nav && nav.parentElement) nav.after(toast); else document.body.prepend(toast);
    toast.dataset.ubicado = '1';
    // Las vistas de pantalla completa (el kiosco es una capa fija) ponen la franja arriba y arrancan debajo de su alto.
    const alto = () => document.documentElement.style.setProperty('--nx-aviso-alto', Math.ceil(toast.getBoundingClientRect().height) + 'px');
    alto();
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(alto).observe(toast);
  };
  mover();
  new MutationObserver(mover).observe(document.body, { childList: true });
}

/** Arranca el vigia una sola vez por pestana. */
export function iniciarAvisoVersionNueva() {
  if (iniciado) return;
  iniciado = true;
  ubicarAvisoVersion();
  // AVISO-VERSION-CLIENTE-T1 (#43980): admin y dev ven el aviso con Recargar; el cliente de un negocio no ve nada y la
  // version nueva entra sola en el siguiente cambio de pantalla, si ninguna vista tiene algo sin guardar.
  let pendiente = false;
  const alAviso = (servida) => { if (modoDeAviso(AppConfig.role) === 'boton') mostrarAviso(servida); else pendiente = true; };
  window.addEventListener('hashchange', () => {
    if (puedeRecargarAlNavegar({ pendiente, haySinGuardar: haySinGuardar() })) window.location.reload();
  });
  const vigia = crearVigia({ corriendo: CORRIENDO, leer: leerVersionServida, alAviso });
  // VISTA-VIEJA-DEPLOY-T1 (#43873): si version.json no responde (la pagina pudo salir del index guardado por el
  // service worker en la ventana del deploy), se vuelve a preguntar a los 30 s, un solo reintento pendiente a la vez.
  let reintento = null;
  const revisarYReintentar = async () => {
    await vigia.revisar();
    if (vigia.fallo && !reintento) reintento = setTimeout(() => { reintento = null; revisarYReintentar(); }, REINTENTO_MS);
  };
  setInterval(() => { vigia.revisar(); }, CADA_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') revisarYReintentar();
  });
  // La primera a los 3 s (antes 30 s): una copia vieja se nota al entrar, sin competir con la carga de la pantalla.
  setTimeout(revisarYReintentar, 3 * 1000);
}

/**
 * Si falla la carga de un modulo (vite:preloadError: un pedazo de un build que ya no esta en el servidor), se recarga
 * una sola vez por build, con la marca en sessionStorage antes de recargar. Sin sessionStorage no se recarga (no hay
 * como frenar un ciclo): el error sigue y el aviso de version dice que hay una nueva.
 */
/** Q #43998: la pantalla no abrio por un pedazo del build viejo y hay algo sin guardar: se dice que hacer, sin recargar. */
function avisarGuardarPrimero() {
  if (document.getElementById('nx-sw-toast')) return;
  const toast = document.createElement('div');
  toast.id = 'nx-sw-toast';
  toast.setAttribute('role', 'status');
  toast.setAttribute('data-version-guardar', '');
  toast.innerHTML = '<span>Guarda tu presupuesto y vuelve a abrir esta pantalla.</span><button id="nx-version-entendido">Entendido</button>';
  document.body.appendChild(toast);
  document.getElementById('nx-version-entendido').onclick = () => toast.remove();
}

export function recargarSiFallaUnModulo() {
  window.addEventListener('vite:preloadError', (e) => {
    const CLAVE = 'nx_recarga_por_modulo';
    let marca = null;
    let hay = true;
    try { marca = sessionStorage.getItem(CLAVE); } catch { hay = false; }
    const accion = queHacerPorModulo({ marca, commit: CORRIENDO.commit, hayAlmacenamiento: hay, haySinGuardar: haySinGuardar() });
    if (accion === 'avisar') { avisarGuardarPrimero(); return; }
    if (accion !== 'recargar') return;
    try { sessionStorage.setItem(CLAVE, CORRIENDO.commit); } catch { return; }
    e.preventDefault();
    window.location.reload();
  });
}
